import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, User, MapPin, TrendingUp, AlertCircle, WifiOff, FileQuestion } from 'lucide-react';
import { fetchAuthRiskVectorUsers, clearAuthRiskVectorUsers } from '../../../../store/slices/dashboardSlice';
import './AuthRiskVectorModal.css';
import {Loading} from "../../../common"
import formatNumber from '../../../../utils/formatNumber';

const AuthRiskVectorModal = ({ isOpen, onClose, vectorData }) => {
  const dispatch = useDispatch();
  
  const { data, loading, error } = useSelector((state) => state.dashboard.authRiskVectorUsers);
  useEffect(() => {
    if (isOpen && vectorData?.rule_name) {
      dispatch(fetchAuthRiskVectorUsers(vectorData.rule_name));
    }
  }, [isOpen, vectorData, dispatch]);

  useEffect(() => {
    // Clear data when modal closes
    if (!isOpen) {
      dispatch(clearAuthRiskVectorUsers());
    }
  }, [isOpen, dispatch]);

  const handleRetry = () => {
    if (vectorData?.rule_name) {
      dispatch(fetchAuthRiskVectorUsers(vectorData.rule_name));
    }
  };

  const renderErrorState = () => {
    if (!error) return null;

    const errorMessage = error.message || error;
    const errorType = error.type || 'unknown';

    // Check for specific error messages
    const isNoRulesError = errorMessage.toLowerCase().includes('no rules found') || 
                          errorMessage.toLowerCase().includes('no users found') ||
                          errorMessage.toLowerCase().includes('not found');
    
    const isNetworkError = errorType === 'network_error' || 
                          errorMessage.toLowerCase().includes('network') ||
                          errorMessage.toLowerCase().includes('fetch');

    if (isNoRulesError) {
      return (
        <div className="auth-risk-error-state no-data">
          <div className="error-icon-wrapper">
            <FileQuestion size={48} strokeWidth={1.5} />
          </div>
          <h3 className="error-title">No Data Available</h3>
          <p className="error-message">
            {errorMessage || 'No rules found for this authentication risk vector.'}
          </p>
          <p className="error-suggestion">
            This might mean there are currently no triggered instances for this rule.
          </p>
          <button className="close-button" onClick={onClose}>
            Close
          </button>
        </div>
      );
    }

    if (isNetworkError) {
      return (
        <div className="auth-risk-error-state network-error">
          <div className="error-icon-wrapper">
            <WifiOff size={48} strokeWidth={1.5} />
          </div>
          <h3 className="error-title">Connection Error</h3>
          <p className="error-message">
            Unable to connect to the server. Please check your internet connection.
          </p>
          <button className="retry-button" onClick={handleRetry}>
            Retry
          </button>
        </div>
      );
    }

    return (
      <div className="auth-risk-error-state general-error">
        <div className="error-icon-wrapper">
          <AlertCircle size={48} strokeWidth={1.5} />
        </div>
        <h3 className="error-title">Something Went Wrong</h3>
        <p className="error-message">{errorMessage}</p>
        {error.statusCode && (
          <p className="error-code">Error Code: {error.statusCode}</p>
        )}
        <button className="retry-button" onClick={handleRetry}>
          Try Again
        </button>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="auth-risk-modal-overlay" onClick={onClose}>
      <div className="auth-risk-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="auth-risk-modal-header">
          <div className="auth-risk-modal-title-section">
            <h2>{vectorData?.rule_name || 'Authentication Risk Vector'}</h2>
            {data && !error && (
              <span className="auth-risk-total-count">
                {formatNumber(data.total_count)} total occurrences
              </span>
            )}
          </div>
          <button className="auth-risk-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="auth-risk-modal-body">
          {loading ? (
            <Loading text="loading identities"/>
          ) : error ? (
            renderErrorState()
          ) : data?.users?.length > 0 ? (
            <>
              <div className="auth-risk-summary">
                <div className="summary-card">
                  <User size={20} />
                  <div>
                    <p className="summary-label">Affected Users</p>
                    <p className="summary-value">{formatNumber(data.users.length)}</p>
                  </div>
                </div>
                <div className="summary-card">
                  <TrendingUp size={20} />
                  <div>
                    <p className="summary-label">Total Triggers</p>
                    <p className="summary-value">{formatNumber(data.total_count)}</p>
                  </div>
                </div>
              </div>

              <div className="auth-risk-users-list">
                <h3 className="users-list-title">Affected Identities</h3>
                {data.users.map((user, index) => (
                  <div key={index} className="auth-risk-user-card">
                    <div className="user-card-header">
                      <div className="user-avatar">
                        <User size={20} />
                      </div>
                      <div className="user-info">
                        <p className="user-email">{user.actor_email}</p>
                        <div className="user-meta">
                          <span className="meta-item">
                            <MapPin size={14} />
                            {user.ip_address}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="user-trigger-badge">
                      <span>{formatNumber(user.trigger_count)}</span>
                      <span className="badge-label">triggers</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="auth-risk-empty-state">
              <User size={48} />
              <p>No users found for this vector</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthRiskVectorModal;